import { lenis, prefersReducedMotion } from '@scripts/lenis';
import gsap from 'gsap';

// キャッシュ済みで一瞬で終わる場合でもチラつかせないための最短表示時間
const MIN_VISIBLE_MS = 500;
// 画像や WebGL が応答しない (節目が来ない) 場合に解除する安全弁。three.js
// (約 563KB) の読み込みも待つため、細い回線を途中で切らないよう長めに取る
const SAFETY_MS = 20000;
// desktop / mobile 画像の切替ブレークポイント。Hero.astro と揃える
const MOBILE_QUERY = '(max-width: 1023px)';
// "Loading..." / 進捗率のセリフ体を待つ上限。これを過ぎたらフォールバック書体の
// まま出す (文字が出ないままになるより良い)
const FONT_WAIT_MS = 1500;
// 文字オーバーレイで実際に使われるフェイス。@fontsource/cormorant-garamond の
// 600-italic だけを読み込んでいるので、指定も 600 italic に合わせる
const TEXT_FONT = 'italic 600 1rem "Cormorant Garamond"';

// ホームのヒーローが表示できるまでローディング画面を出す。進捗は「完了した
// 節目の数」を目標にイージングで近づける方式。節目は (1) ヒーロー画像 3 枚の
// load と、(2) WebGL リップル層の読み込み — three.js チャンク(約 563KB)到着
// (soak:hero-ripple-loaded)と初回描画完了(soak:hero-ripple-ready)。
//
// バイト実測(fetch ストリーム)は、ブラウザが <img>/import で読むのと同じ URL
// と統合(coalescing)されて途中経過が取れず 0→100 に飛ぶブラウザがあるため
// 使わない。節目の間は ease-out + トリクルで直線的にならないよう補間する。
// WebGL を待たない場合(reduced-motion / canvas 無し / 単枚)は画像のみを節目にする。
export function initHeroLoader() {
  const el = document.querySelector<HTMLElement>('[data-hero-loader]');
  if (!el) return; // ホーム以外では存在しない

  // 再訪 (過去に完了 → localStorage soak:hero-seen → head で html.hero-seen)。
  // 手の込んだ演出は出さず、無地カバーで画像 readiness だけ待って即リビールする。
  // 演出パーツの伏せは global.css が担う
  const seen = document.documentElement.classList.contains('hero-seen');

  // 各接合線。担当区間 [start, end) ぶんだけ scaleX で伸びる。中央線
  // (data-fill-center) だけは正確なプログレスへ即追従させ、他の線は中心からの
  // 距離 + ランダムなばらつきで少し遅れて追いつく (staggered)。遅延追従は
  // gsap.quickTo で行い、中央線は render で直接 scaleX を書く。
  //
  // 1 本の接合線は「上のパネルの下端に付く影」と「下のパネルの上端に付く
  // ハイライト」の 2 要素に分かれて別パネルに属する (HeroLoader.astro 参照)。
  // 2 要素は 1 本の切り口なので必ず同じタイミングで伸ばす必要があり、遅延追従の
  // duration は要素ごとではなく接合線 (data-seam) ごとに決める。同じ
  // duration/ease の quickTo に同じ値を同じタイミングで渡せば 2 要素は同期する
  const fillNodes = [
    ...el.querySelectorAll<HTMLElement>('[data-hero-loader-fill]'),
  ];
  const centerSeam = Number(
    el.querySelector<HTMLElement>('[data-fill-center]')?.dataset.seam ?? -1,
  );
  // 接合線ごとの duration。中央からの距離が大きいほど遅く、さらに毎回ランダムな
  // ばらつきを足す。同じ seam の 2 要素で同一値を使い回す
  const seamDurations = new Map<number, number>();
  const durationFor = (seam: number) => {
    const cached = seamDurations.get(seam);
    if (cached !== undefined) return cached;
    const dist = centerSeam < 0 ? 0 : Math.abs(seam - centerSeam);
    const duration = 0.3 + dist * 0.18 + Math.random() * 0.25;
    seamDurations.set(seam, duration);
    return duration;
  };
  const fills = fillNodes.map((node) => {
    const seam = Number(node.dataset.seam ?? 0);
    const isCenter = seam === centerSeam;
    return {
      node,
      start: Number(node.dataset.fillStart ?? 0),
      end: Number(node.dataset.fillEnd ?? 1),
      isCenter,
      // 中央線は setter 不要 (render が直接書く)。他は遅延追従の setter を持つ
      setScaleX: isCenter
        ? null
        : gsap.quickTo(node, 'scaleX', {
            duration: durationFor(seam),
            ease: 'power2.out',
          }),
    };
  });
  const percentEl = el.querySelector<HTMLElement>('[data-hero-loader-percent]');
  const panels = el.querySelectorAll<HTMLElement>('[data-hero-loader-panel]');
  const textEl = el.querySelector<HTMLElement>('[data-hero-loader-text]');

  // 対象 URL は <picture data-hero-slide> から取り出す (hero.ts と同じ読み方)。
  // 画面幅に応じて desktop(img src) / mobile(source srcset) を選ぶ
  const useMobile = window.matchMedia(MOBILE_QUERY).matches;
  const urls = [...document.querySelectorAll<HTMLElement>('[data-hero-slide]')]
    .map((pic) => {
      const desktop = pic.querySelector('img')?.getAttribute('src') ?? '';
      const mobile =
        pic.querySelector('source')?.getAttribute('srcset') ?? desktop;
      return useMobile ? mobile : desktop;
    })
    .filter((u) => u.length > 0);

  // WebGL(hero-ripple + three.js)の節目を待つか。hero.ts の読み込み条件に
  // 合わせる: reduced-motion 以外・canvas あり・スライド複数。満たすときだけ
  // three.js 到着 / 初回描画 の 2 節目を総数に含める。再訪 (seen) は three.js
  // (~563KB) を待たず画像だけを節目にし、カバーを早く畳む (ripple は
  // initHeroSlider が従来どおり読み込む)
  const expectWebGL =
    !seen &&
    !prefersReducedMotion &&
    !!document.querySelector('[data-hero-canvas]') &&
    urls.length > 1;

  const total = urls.length + (expectWebGL ? 2 : 0);
  const startedAt = performance.now();
  let finished = false;
  let loadedCount = 0;
  let trickleTimer = 0;
  let safetyTimer = 0;

  // "Loading..." と進捗率は初期状態が opacity:0 (global.css)。セリフ体が使える
  // ようになってからフェードインさせ、フォールバック書体からの差し替え (FOUT) が
  // 見えないようにする。フォントが届かない/失敗する環境でも文字が出ないままには
  // しないよう FONT_WAIT_MS で打ち切る。すでに完了して畳み始めていたら出さない
  let textRevealed = false;
  const revealText = () => {
    if (!textEl || textRevealed || finished) return;
    textRevealed = true;
    gsap.to(textEl, {
      autoAlpha: 1,
      duration: prefersReducedMotion ? 0 : 0.4,
      ease: 'power2.out',
    });
  };
  void (async () => {
    try {
      await Promise.race([
        document.fonts?.load(TEXT_FONT) ?? Promise.resolve(),
        new Promise((resolve) => window.setTimeout(resolve, FONT_WAIT_MS)),
      ]);
    } catch {
      /* 読み込み失敗はフォールバック書体で出せばよい */
    }
    revealText();
  })();

  // 表示中は Lenis と native スクロールを止める (CSS の overflow:hidden と併用)
  lenis?.stop();

  // 表示値。0..1。reduced motion は即時反映、それ以外は tween でなめらかに動かす
  const progress = { value: 0 };
  const render = () => {
    const v = Math.min(Math.max(progress.value, 0), 1);
    fills.forEach(({ node, start, end, isCenter, setScaleX }) => {
      const span = end - start;
      const seg =
        span > 0
          ? Math.min(Math.max((v - start) / span, 0), 1)
          : v >= end
            ? 1
            : 0;
      // 中央線は即・正確に。他は quickTo で遅れて追いつく
      if (isCenter || !setScaleX) node.style.transform = `scaleX(${seg})`;
      else setScaleX(seg);
    });
    if (percentEl) percentEl.textContent = `${Math.round(v * 100)}%`;
  };

  // 目標値へ前進 (後退させない)。序盤速く終盤ゆっくりの ease-out で自然に近づける
  const easeTo = (t: number, duration = 0.9) => {
    const target = Math.min(Math.max(t, progress.value), 1);
    if (prefersReducedMotion) {
      progress.value = target;
      render();
      return;
    }
    gsap.to(progress, {
      value: target,
      duration,
      ease: 'power2.out',
      onUpdate: render,
      overwrite: true,
    });
  };

  // トリクル。次のマイルストーン手前 (ceiling) まで減速しながらじわっと進め、
  // 実際の読み込みを追い越さない。0% に張り付かず「動いている」印象を保つ
  const trickle = () => {
    if (finished) return;
    const ceiling = Math.min((loadedCount + 0.9) / total, 0.95);
    if (progress.value < ceiling) {
      easeTo(progress.value + (ceiling - progress.value) * 0.3, 0.6);
    }
    trickleTimer = window.setTimeout(trickle, 600);
  };

  const finish = () => {
    if (finished) return;
    finished = true;
    window.clearTimeout(trickleTimer);
    window.clearTimeout(safetyTimer);

    // 以降の訪問は演出を省くため、完了フラグを保存する (初回で記録、再訪でも
    // idempotent)。localStorage 例外 (Safari プライベート等) は握りつぶす
    try {
      localStorage.setItem('soak:hero-seen', '1');
    } catch {
      /* ストレージ不可なら次回もフル演出になるだけで無害 */
    }

    // 再訪 (seen) は最短表示を待たず即詰める。初回はチラつき防止の下限を守る
    const elapsed = performance.now() - startedAt;
    const delay = seen ? 0 : Math.max(0, MIN_VISIBLE_MS - elapsed);

    // 最短表示時間を担保してから 100% まで詰め、文字をフェードアウトしてから
    // 6 パネルを市松状に左右へ互い違いにスライドアウトさせてヒーローを見せる。
    // 要素を DOM から消すことで global.css の :has() スクロールロックも解ける。
    // 除去後に soak:hero-loader-done を発火し、ヒーロー側 (hero.ts) がイントロと
    // スライダー自動再生の開始タイミングに使う
    const done = () => {
      // reveal (ヒーロー側のイントロ/スライダー開始トリガー) と要素の除去を分離。
      // reveal はパネルが抜けきる少し手前で先行発火し、ロゴ等の登場を前倒しする。
      // 除去とスクロールロック解除はタイムライン完了時のまま
      const reveal = () =>
        window.dispatchEvent(new Event('soak:hero-loader-done'));
      const remove = () => {
        el.remove();
        lenis?.start();
      };
      if (prefersReducedMotion) {
        progress.value = 1;
        render();
        reveal();
        remove();
      } else if (seen) {
        // 再訪: 帯スライドの演出は行わず、無地カバーを短くフェードするだけ。
        // 画像 readiness は待った後なのでポップインしない。
        //
        // 画像キャッシュ済みだと finish→done がこの initHeroLoader 実行中に同期で
        // 走り、reveal を即発火すると index.ts で後続の initHero/initHeroSlider が
        // リスナーを張る前になり取りこぼす。microtask に逃がして同期処理 (リスナー
        // 登録) の完了後に発火させる
        progress.value = 1;
        render();
        queueMicrotask(reveal);
        gsap.to(el, {
          autoAlpha: 0,
          duration: 0.2,
          ease: 'power2.out',
          onComplete: remove,
        });
      } else {
        // 各パネルは data-dir (1=右 / -1=左) の向きへ、確実に画面外へ抜ける
        // 距離 (ビューポート幅 + 余白) だけ移動する
        const dist = window.innerWidth * 1.05;
        gsap
          .timeline({ onComplete: remove })
          // まず接合線を右端まで詰め、% を 100% にする
          .to(progress, {
            value: 1,
            duration: 0.3,
            ease: 'power2.out',
            overwrite: true,
            onUpdate: render,
          })
          // シャッターが割れる前に文字を消す
          .to(
            textEl,
            { autoAlpha: 0, duration: 0.3, ease: 'power2.out' },
            '+=0.2',
          )
          // 6 パネルを互い違いにスライドアウト
          .to(
            panels,
            {
              x: (_i, target: HTMLElement) => Number(target.dataset.dir) * dist,
              duration: 1,
              ease: 'power3.inOut',
            },
            '<',
          )
          // フェードアウトはスライドに少し遅れて始め、抜けきる頃に消え切る
          .to(
            panels,
            { autoAlpha: 0, duration: 0.3, ease: 'power2.in' },
            '-=0.4',
          )
          // パネルが抜けきる 0.25s 手前でヒーローの reveal を先行開始する
          .call(reveal, undefined, '-=0.5');
      }
    };

    if (delay > 0) window.setTimeout(done, delay);
    else done();
  };

  // 読み込むものが無ければ即終了 (念のため)
  if (total === 0) {
    finish();
    return;
  }

  // 1 節目ぶん前進。目標 (完了節目数 / 総数) へイージングで近づける
  const advance = () => {
    loadedCount += 1;
    easeTo(loadedCount / total);
    if (loadedCount >= total) finish();
  };

  // 各画像の読み込み完了を待つ。new Image() は同一 URL でリクエスト/キャッシュを
  // 共有するので二重ダウンロードにならず、load / error だけを確実に拾える
  urls.forEach((url) => {
    const img = new Image();
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      advance();
    };
    img.onload = settle;
    img.onerror = settle; // 失敗もブロックしない (通常の DOM フォールバックに任せる)
    img.src = url;
    if (img.complete) settle(); // すでにキャッシュ済みなら即完了
  });

  // WebGL の節目。hero.ts が dispatch する (失敗時も通知される)。once で二重加算防止
  if (expectWebGL) {
    window.addEventListener('soak:hero-ripple-loaded', advance, { once: true });
    window.addEventListener('soak:hero-ripple-ready', advance, { once: true });
  }

  // 未完了なら演出を始める。全キャッシュ済みで finish 済みなら何もしない
  if (!finished && !prefersReducedMotion) trickle();
  if (!finished) safetyTimer = window.setTimeout(finish, SAFETY_MS);
}
